/**
 * Service d'intégration des événements AI extraits des notes
 * Enrichit les individualsCache avec les événements sans bloquer l'UI
 */

import aiNotesService from './aiNotesService.js';
import { groupNotesByIndividual } from './individualContextBuilder.js';
import { storeEvents, EVENTS } from '../common/stores/storeEvents.js';
import gedcomDataStore from '../gedcom/stores/gedcomDataStore.js';
import configStore from '../tabs/fanChart/fanConfigStore.js';
import notificationManager from '../utils/NotificationManager.js';

// Consentement à l'extraction IA, mémorisé par fichier (fileId files-service).
// Valeurs : 'granted' | 'denied'. Absent = jamais demandé (ou fermé sans choisir).
const AI_CONSENT_STORAGE_KEY = 'geneafan_ai_extraction_consent';

// Clé de lieu au format du parser (slug : minuscules, sans accents, tirets).
// Aligné sur les clés GEDCOM pour que "Ribeauvillé" rejoigne l'entrée
// "ribeauville" du store des villes au lieu d'en créer une jumelle accentuée.
function locationToKey(location) {
    return location
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-');
}
class AiNotesIntegration {
    constructor() {
        this.isProcessing = false;
        this.processedIndividuals = new Map();
        this.extractionQueue = [];
    }

    /**
     * Lance l'intégration complète des événements AI
     * V8: Regroupe les notes par individu avec contexte chronologique
     * @param {Array<string>} selectedNoteIds - Optionnel : liste des noteIds à traiter. Si omis, traite toutes les notes.
     */
    async integrateAiEventsIntoCache(selectedNoteIds = null, useCache = true) {
        if (this.isProcessing) {
            console.warn('AI integration already in progress');
            return;
        }

        try {
            this.isProcessing = true;
            console.group(`🤖 Starting AI events integration (grouped by individual)${useCache ? '' : ' [CACHE BYPASS]'}`);

            const { notesCache, individualsCache } = gedcomDataStore;

            if (!notesCache || notesCache.size === 0) {
                console.log('No notes found in GEDCOM - skipping AI extraction');
                return;
            }

            // 🆕 Filtrer les notes selon la sélection
            let notesCacheToProcess = notesCache;
            if (selectedNoteIds && selectedNoteIds.length > 0) {
                const selectedSet = new Set(selectedNoteIds);
                notesCacheToProcess = new Map(
                    Array.from(notesCache.entries()).filter(([noteId]) => selectedSet.has(noteId))
                );
                console.log(`📋 Processing ${notesCacheToProcess.size} selected notes (out of ${notesCache.size} total)`);
            }

            // Regrouper les notes par individu avec contexte chronologique
            const grouped = groupNotesByIndividual(notesCacheToProcess, individualsCache);

            if (grouped.size === 0) {
                console.log('No eligible notes found after grouping - skipping AI extraction');
                return;
            }

            const totalNotes = Array.from(grouped.values()).reduce((sum, g) => sum + g.notes.length, 0);
            console.log(`🚀 Processing ${grouped.size} individuals (${totalNotes} notes)`);

            // Traiter via le Cloudflare Worker (flux groupé par individu)
            const result = await aiNotesService.processAllGrouped(grouped, useCache);

            if (result.success) {
                // Enrichir directement chaque individu avec ses événements
                const { eventsByIndividual } = result;
                let totalEventsAdded = 0;
                let totalEventsEnriched = 0;
                let individualsModified = 0;

                for (const [individualId, events] of Object.entries(eventsByIndividual)) {
                    if (!events || events.length === 0) continue;

                    const before = (individualsCache.get(individualId)?.e?.length || 0);
                    await this.enrichIndividualWithEvents(individualId, events, individualsCache);
                    const after = (individualsCache.get(individualId)?.e?.length || 0);
                    const added = after - before;
                    const enriched = this.processedIndividuals.get(individualId)?.eventsEnriched || 0;
                    totalEventsAdded += added;
                    totalEventsEnriched += enriched;
                    if (added > 0 || enriched > 0) individualsModified++;

                    await this.yieldToUI();
                }

                console.log(`✅ AI integration completed: ${totalEventsAdded} events added, ${totalEventsEnriched} events enriched across ${individualsModified} individuals`);

                storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_COMPLETED, {
                    totalEvents: result.summary?.total_events_extracted || 0,
                    totalEventsAdded,
                    totalEventsEnriched,
                    individualsEnriched: individualsModified,
                    successRate: Math.round((result.summary?.success_rate || 0) * 100)
                });
            } else {
                throw new Error(result.error);
            }

        } catch (error) {
            console.error('AI integration failed:', error);
            storeEvents.emit(EVENTS.AI_NOTES.PROCESSING_FAILED, { error: error.message });
        } finally {
            this.isProcessing = false;
            console.groupEnd();
        }
    }

    /**
     * Enrichit un individu spécifique avec les événements AI
     * Les événements sont ajoutés directement dans le tableau `e` (format unifié)
     */
    async enrichIndividualWithEvents(individualId, aiEvents, individualsCache) {
        const individual = individualsCache.get(individualId);
        if (!individual) {
            console.warn(`Individual ${individualId} not found in cache`);
            return;
        }

        if (!individual.e) {
            individual.e = [];
        }

        // Purge previous notes-AI events before re-integration to avoid duplicates.
        // Wikipedia/document events are NOT purged: since the .geneatree
        // auto-save they are durable, user-validated data — purging them here
        // destroyed persisted Wikipedia events on notes re-extraction (and the
        // auto-save then persisted the loss). Their own re-extraction flows
        // handle their own purge (per-source _getPurgeFilter in the panels).
        const before = individual.e.length;
        individual.e = individual.e.filter(e => e.m?.src !== 'ai');
        const purged = before - individual.e.length;
        if (purged > 0) {
            console.log(`🧹 ${individualId}: purged ${purged} previous AI (notes) events`);
        }

        const geneafanEvents = aiEvents.map(aiEvent => this.convertAiEventToGeneafan(aiEvent));
        let addedCount = 0;
        let enrichedCount = 0;

        for (const event of geneafanEvents) {
            if (event._enriches) {
                // ENRICHISSEMENT : trouver l'événement existant et fusionner les métadonnées
                const enriched = this.enrichExistingEvent(event, individual.e);
                if (enriched) {
                    enrichedCount++;
                } else {
                    // Fallback : pas de match → ajouter comme nouvel événement
                    delete event._enriches;
                    if (!this.isDuplicateEvent(event, individual.e)) {
                        individual.e.push(event);
                        addedCount++;
                    }
                }
            } else {
                // STANDARD : ajouter comme nouvel événement avec vérification doublons
                if (!this.isDuplicateEvent(event, individual.e)) {
                    individual.e.push(event);
                    addedCount++;
                }
            }
        }

        this.processedIndividuals.set(individualId, {
            eventsAdded: addedCount,
            eventsEnriched: enrichedCount,
            timestamp: Date.now()
        });
    }

    /**
     * Trouve un événement existant correspondant au type ciblé et fusionne les métadonnées AI.
     * @param {Object} aiEvent - Événement AI avec _enriches (type compressé, ex: "fd")
     * @param {Array} existingEvents - Tableau individual.e
     * @returns {boolean} true si un événement a été enrichi
     */
    enrichExistingEvent(aiEvent, existingEvents) {
        const targetType = aiEvent._enriches;
        const candidates = existingEvents.filter(e => e.t === targetType);

        if (candidates.length === 0) return false;

        // Sélection du candidat : un seul → direct, plusieurs → date la plus proche
        let target;
        if (candidates.length === 1) {
            target = candidates[0];
        } else if (aiEvent.d) {
            target = candidates.reduce((closest, c) =>
                c.d && Math.abs(c.d - aiEvent.d) < Math.abs((closest.d || 0) - aiEvent.d)
                    ? c : closest
            , candidates[0]);
        } else {
            target = candidates[0];
        }

        // Fusionner les métadonnées AI dans l'événement existant
        if (!target.m) target.m = {};
        target.m.ai_enriched = true;
        if (aiEvent.m?.sum) target.m.sum = aiEvent.m.sum;
        if (aiEvent.m?.conf) target.m.conf = aiEvent.m.conf;
        if (aiEvent.m?.dp) target.m.dp = aiEvent.m.dp;
        if (aiEvent.m?.org) target.m.org = aiEvent.m.org;
        if (aiEvent.m?.pf) target.m.pf = aiEvent.m.pf;
        if (aiEvent.m?.ps) target.m.ps = aiEvent.m.ps;
        if (aiEvent.m?.at && !target.m.at) target.m.at = aiEvent.m.at;

        // Lieu : ajouter seulement si l'existant n'en a pas
        if (aiEvent.l && !target.l) target.l = aiEvent.l;

        // Date : transférer seulement si l'existant n'en a pas
        if (aiEvent.d && !target.d) target.d = aiEvent.d;
        if (aiEvent.d2 && !target.d2) target.d2 = aiEvent.d2;

        return true;
    }

    /**
     * Enrichit un événement existant à un index donné avec les données d'un événement Wikipedia.
     * Utilisé par le pipeline de réconciliation AI (enrichmentPanel).
     * @param {Object} targetEvent - Événement existant dans individual.e (par référence)
     * @param {Object} sourceEvent - Événement Wikipedia converti au format geneafan
     * @param {Object} [mergedFields] - Champs spécifiques à fusionner (fournis par la réconciliation AI)
     */
    enrichExistingEventFromWikipedia(targetEvent, sourceEvent, mergedFields) {
        if (!targetEvent.m) targetEvent.m = {};

        // Mark as enriched (source-agnostic)
        targetEvent.m.ai_enriched = true;
        if (sourceEvent.m?.src) {
            targetEvent.m.src_enriched = sourceEvent.m.src;
        }

        // Helper: pick the longer/richer string value
        const richer = (existing, incoming) => {
            if (!incoming) return existing;
            if (!existing) return incoming;
            // Incoming is longer → probably more detailed
            return incoming.length > existing.length ? incoming : existing;
        };

        if (mergedFields) {
            // Summary: prefer richer (longer) version
            if (mergedFields.summary) {
                targetEvent.m.sum = richer(targetEvent.m.sum, mergedFields.summary);
            }
            // Metadata: fill missing, upgrade with richer source
            if (mergedFields.organization) {
                targetEvent.m.org = richer(targetEvent.m.org, mergedFields.organization);
            }
            if (mergedFields.profession) {
                targetEvent.m.pf = richer(targetEvent.m.pf, mergedFields.profession);
            }
            if (mergedFields.position) {
                targetEvent.m.ps = richer(targetEvent.m.ps, mergedFields.position);
            }
            if (mergedFields.activity_type && !targetEvent.m.at) {
                targetEvent.m.at = mergedFields.activity_type;
            }
            if (mergedFields.location && !targetEvent.l) {
                targetEvent.l = locationToKey(mergedFields.location);
            }
            // Date enrichment — handle periods and precision upgrades
            const newDate = mergedFields.date ? this.normalizeDateForGeneafan(mergedFields.date) : null;
            const newDateEnd = mergedFields.date_end ? this.normalizeDateForGeneafan(mergedFields.date_end) : null;

            if (newDate && newDateEnd) {
                // Period: source provides a range (e.g. 1920→1922)
                // Existing has a single date (e.g. 1922) → adopt the full period
                if (!targetEvent.d2) {
                    targetEvent.d = newDate;     // start of period
                    targetEvent.d2 = newDateEnd; // end of period
                }
            } else if (newDate) {
                if (!targetEvent.d) {
                    targetEvent.d = newDate;
                } else {
                    // Replace year-only date with more precise one
                    const existStr = targetEvent.d.toString();
                    const newStr = newDate.toString();
                    if (existStr.endsWith('0101') && !newStr.endsWith('0101')) {
                        targetEvent.d = newDate;
                    }
                }
            }
            if (newDateEnd && !targetEvent.d2) {
                targetEvent.d2 = newDateEnd;
            }
        } else {
            // Fallback: merge from source event, richer values win
            targetEvent.m.sum = richer(targetEvent.m.sum, sourceEvent.m?.sum);
            targetEvent.m.org = richer(targetEvent.m.org, sourceEvent.m?.org);
            if (sourceEvent.m?.pf && !targetEvent.m.pf) targetEvent.m.pf = sourceEvent.m.pf;
            if (sourceEvent.m?.ps && !targetEvent.m.ps) targetEvent.m.ps = sourceEvent.m.ps;
            if (sourceEvent.m?.at && !targetEvent.m.at) targetEvent.m.at = sourceEvent.m.at;
            if (sourceEvent.l && !targetEvent.l) targetEvent.l = sourceEvent.l;
            if (sourceEvent.d && !targetEvent.d) targetEvent.d = sourceEvent.d;
            if (sourceEvent.d2 && !targetEvent.d2) targetEvent.d2 = sourceEvent.d2;
        }
    }

    /**
     * Nettoie un événement AI brut avant conversion au format GeneaFan.
     * Corrige les erreurs fréquentes du modèle :
     * - Nom d'entreprise dans le champ position (ex: "Directeur général de la Banque Worms")
     * - Nom d'entreprise dans le champ location (ex: location:"Banque Worms")
     * - Affectation militaire classée comme residence au lieu de military
     * - Location avec suffixe pays inutile (ex: "Paris, France")
     */
    sanitizeAiEvent(aiEvent) {
        const event = { ...aiEvent };

        // 1. Reclassifier residence → military si mots-clés militaires présents
        if (event.type === 'residence') {
            const text = [event.label, event.summary, event.organization].filter(Boolean).join(' ');
            if (/\b(affecté|militaire|unité|marine|armée|garnison|régiment|bataillon|escadron|embarqu|caserne)/i.test(text)) {
                event.type = 'military';
            }
        }

        // 2. Nettoyer position : retirer le nom de l'organisation
        if (event.position && event.organization) {
            event.position = this.extractTitleFromPosition(event.position, event.organization);
        }

        // 3. Nettoyer location : retirer les noms d'entreprises/organisations
        if (event.location) {
            if (event.organization && this.isLikelyOrganization(event.location, event.organization)) {
                event.location = null;
            } else {
                // Retirer les suffixes pays courants pour obtenir une clé propre
                event.location = event.location
                    .replace(/,\s*(france|sénégal|maroc|algérie|tunisie|belgique|suisse|allemagne|espagne|italie|royaume-uni|états-unis|usa|uk)\s*$/i, '')
                    .trim();
            }
        }

        return event;
    }

    /**
     * Checks if a summary is redundant given structured fields (lbl, pf, ps, org).
     * Extracts content words (removing French stop words/articles) and checks overlap.
     * "Président du Bureau des Longitudes" vs [ps:"Président", org:"Bureau des Longitudes"] → true
     * "Nommé Ingénieur en chef après la suppression de l'Astronomie" vs [pf:"Ingénieur en chef", org:"Ponts et Chaussées"] → false
     */
    _isSummaryRedundant(summary, refParts) {
        const STOP_WORDS = new Set([
            'a', 'au', 'aux', 'de', 'des', 'du', 'en', 'et', 'la', 'le', 'les',
            'l', 'd', 'un', 'une', 'par', 'pour', 'dans', 'sur', 'avec', 'son',
            'sa', 'ses', 'ce', 'cette', 'qui', 'que', 'est', 'fut', 'ete',
            'the', 'of', 'and', 'in', 'at', 'to', 'for', 'as', 'his', 'her',
        ]);

        const extractWords = (text) => {
            return text
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/['''\-]/g, ' ')
                .split(/\s+/)
                .filter(w => w.length > 1 && !STOP_WORDS.has(w));
        };

        const summaryWords = extractWords(summary);
        if (summaryWords.length === 0) return true;

        const refWords = new Set(refParts.flatMap(p => extractWords(p)));
        if (refWords.size === 0) return false;

        const newWords = summaryWords.filter(w => !refWords.has(w));
        // Summary is redundant if ≤1 new content word (allow for verb conjugation like "nommé", "dirigé")
        return newWords.length <= 1;
    }

    /**
     * Extrait le titre professionnel/grade en retirant le nom de l'organisation.
     * "Directeur général de la Banque Worms" + org:"Banque Worms" → "Directeur général"
     * "Entre dans le Groupe X" → null (phrase, pas un titre)
     */
    extractTitleFromPosition(position, organization) {
        const orgEscaped = organization.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Retirer l'organisation avec les prépositions françaises de liaison
        const cleaned = position
            .replace(new RegExp(`\\s*(de la|du|de l[''']|des|de|d[''']|chez|à l[''']|à la|au|aux)\\s*${orgEscaped}\\s*$`, 'i'), '')
            .trim();

        // Vérifier que le résultat est un titre (pas une phrase avec verbe)
        if (cleaned && cleaned.length > 1 && !/^(entre|quitte|rejoint|passe|il\s|elle\s|sorti)/i.test(cleaned)) {
            return cleaned;
        }

        // La position était une phrase, pas un titre → retourner null
        return null;
    }

    /**
     * Vérifie si une valeur de location est en fait un nom d'organisation
     */
    isLikelyOrganization(location, organization) {
        const locNorm = location.toLowerCase().replace(/['']/g, "'").trim();
        const orgNorm = organization.toLowerCase().replace(/['']/g, "'").trim();

        // Correspondance directe
        if (locNorm === orgNorm) return true;
        // La location contient le nom complet de l'org (ex: location:"Banque Worms Paris" contient org:"Banque Worms")
        // Mais PAS l'inverse : org:"Unité Marine DAKAR" contient "dakar" mais Dakar est une ville, pas une org
        if (orgNorm.length > 3 && locNorm.includes(orgNorm)) return true;

        // Indicateurs d'organisation dans le texte du lieu
        if (/\b(groupe|compagnie|banque|société|entreprise|cabinet|bureau|commissariat|institut|université|école|direction|ministère|agence)/i.test(locNorm)) {
            return true;
        }

        return false;
    }

    /**
     * Convertit un événement AI au format compressé GeneaFan (même format que les événements GEDCOM)
     * Ajoute m.src = 'ai' pour distinguer l'origine
     */
    convertAiEventToGeneafan(aiEvent) {
        // Nettoyer l'événement AI avant conversion
        const sanitized = this.sanitizeAiEvent(aiEvent);

        const eventType = this.mapAiEventTypeToGeneafan(sanitized.type);
        const event = { t: eventType };

        // Date au format YYYYMMDD numérique
        const date = this.normalizeDateForGeneafan(sanitized.date);
        if (date) {
            event.d = date;
        }

        // Date de fin pour les périodes (ex: "de 1984 à 1987")
        const dateEnd = this.normalizeDateForGeneafan(sanitized.date_end);
        if (dateEnd) {
            event.d2 = dateEnd;
        }

        // Location : uniquement les lieux géographiques validés
        if (sanitized.location) {
            event.l = locationToKey(sanitized.location);
        }

        // Metadata avec indicateur de source AI
        event.m = {
            src: 'ai',
            conf: sanitized.confidence_score || 0.8,
            dp: sanitized.date_precision || 'year'
        };

        // Traitement spécifique par type d'événement
        const typesWithPosition = ['po', 'pm']; // emploi et military ont position + organization
        if (typesWithPosition.includes(eventType)) {
            // 🆕 Phase 6.5: Store profession separately
            if (sanitized.profession) {
                event.m.pf = sanitized.profession;
            }
            // 🆕 Phase 6.5: Store position separately
            if (sanitized.position) {
                event.m.ps = sanitized.position;
            }
            if (sanitized.organization) {
                event.m.org = sanitized.organization;
            }
            // V35: Activity type (career/membership)
            if (sanitized.activity_type) {
                event.m.at = sanitized.activity_type;
            }
        } else {
            // Non-occupation types: keep organization
            if (sanitized.organization) {
                event.m.org = sanitized.organization;
            }
        }

        // Single summary field: store only when it adds context beyond structured fields.
        // Priority: summary > label (summary is richer). For non-occupation events
        // without structured fields, label becomes the summary.
        const bestText = sanitized.summary || sanitized.label || '';
        if (bestText) {
            const refParts = [event.m.pf, event.m.ps, event.m.org].filter(Boolean);
            if (refParts.length > 0 && this._isSummaryRedundant(bestText, refParts)) {
                // Text doesn't add value over structured fields
            } else {
                event.m.sum = bestText;
            }
        }

        // Propagate enriches marker for the enrichment pathway
        if (sanitized.enriches) {
            event._enriches = this.mapAiEventTypeToGeneafan(sanitized.enriches);
        }

        return event;
    }

    /**
     * Mappe les types d'événements AI vers les types GeneaFan compressés.
     * Supporte les types français (pipeline GEDCOM/Claude) et anglais (pipeline Wikipedia/Mistral).
     */
    mapAiEventTypeToGeneafan(aiType) {
        const typeMapping = {
            // French types (GEDCOM AI pipeline — Claude)
            'naissance': 'fb',
            'deces': 'fd',
            'emploi': 'po',
            'diplome': 'eg',
            'education': 'pe',
            'military': 'pm',
            'mariage': 'fm',
            'voyage': 'gt',
            'residence': 'pr',
            'distinction': 'dh',
            'publication': 'mp',
            'rencontre': 'cx',
            'autre': 'cx',
            'retraite': 'pt',
            // English types (Wikipedia pipeline — Mistral)
            // 'divorce' and 'decoration' are spelled the same in both languages
            'birth': 'fb',
            'death': 'fd',
            'occupation': 'po',
            'graduation': 'eg',
            'marriage': 'fm',
            'divorce': 'fv',
            'decoration': 'dd',
            'travel': 'gt',
            'retirement': 'pt',
            'military-service': 'pm',
            'immigration': 'gi',
            'emigration': 'ge',
            'naturalization': 'gn',
            'custom': 'cx',
            'other': 'cx'
        };

        return typeMapping[aiType] || 'cx';
    }

    /**
     * Normalise une date AI pour le format GeneaFan (YYYYMMDD numérique)
     */
    normalizeDateForGeneafan(dateStr) {
        if (!dateStr) return null;

        try {
            // Format YYYY-MM-DD
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                return parseInt(dateStr.replace(/-/g, ''));
            }
            
            // Format YYYY-MM
            if (/^\d{4}-\d{2}$/.test(dateStr)) {
                return parseInt(dateStr.replace(/-/g, '') + '01');
            }
            
            // Format YYYY
            if (/^\d{4}$/.test(dateStr)) {
                return parseInt(dateStr + '0101');
            }

            // Fallback: extraire l'année si possible
            const yearMatch = dateStr.match(/(\d{4})/);
            if (yearMatch) {
                return parseInt(yearMatch[1] + '0101');
            }

            return null;
        } catch (error) {
            console.warn(`Failed to normalize date: ${dateStr}`, error);
            return null;
        }
    }

    /**
     * Vérifie si un événement AI est un doublon d'un événement existant
     * Logique adaptée au fait que les événements GEDCOM sont plus précis que les AI
     */
    isDuplicateEvent(newEvent, existingEvents) {
        const newType = newEvent.t;

        return existingEvents.some(existing => {
            // Même type exact + même date DÉFINIE = doublon certain
            if (existing.t === newType && existing.d && newEvent.d && existing.d === newEvent.d) {
                return true;
            }

            // Mariage AI redondant si un mariage GEDCOM existe déjà (un seul mariage par contexte)
            if (newType === 'fm' && existing.t === 'fm') {
                return true;
            }

            // Graduation AI sans date + graduation GEDCOM existante = doublon probable
            if (newType === 'eg' && existing.t === 'eg' && !newEvent.d) {
                return true;
            }

            // Distinction AI sans date + distinction GEDCOM existante = doublon probable
            if (newType === 'dh' && existing.t === 'dh' && !newEvent.d) {
                return true;
            }

            // Même type + les deux sans date = comparer le contenu textuel
            // Permet : "Assureur" (GEDCOM) + "Directeur général" (AI) = événements distincts
            // Bloque : "Assureur" (GEDCOM) + "Assureur" (AI) = vrai doublon
            // Exception : même position mais orgs différentes = événements distincts
            //   Ex: "Président du Bureau des Longitudes" ≠ "Président de l'Association Française"
            if (existing.t === newType && !existing.d && !newEvent.d) {
                // Compare on the fields actually populated since V31 (pf/ps/sum) —
                // legacy m.v is no longer written by the worker
                const contentOf = (e) => [e.m?.pf, e.m?.ps, e.m?.sum]
                    .filter(Boolean).join(' ').toLowerCase().trim();
                const newContent = contentOf(newEvent);
                const existContent = contentOf(existing);
                if (newContent && existContent && newContent === existContent) {
                    // Si les deux ont une organisation différente, ce ne sont pas des doublons
                    // Comparaison fuzzy pour gérer les typos AI (doubles consonnes, accents)
                    const newOrg = (newEvent.m?.org || '').trim();
                    const existOrg = (existing.m?.org || '').trim();
                    if (newOrg && existOrg && !this._fuzzyOrgMatch(newOrg, existOrg)) {
                        return false;
                    }
                    return true;
                }
            }

            return false;
        });
    }

    /**
     * Comparaison fuzzy de noms d'organisation.
     * Gère les typos AI (doubles consonnes, accents manquants).
     * "Club Méditerrannée" = "Club Méditerranée"
     */
    _fuzzyOrgMatch(name1, name2) {
        const normalize = (s) => s.toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/(.)\1+/g, '$1');
        const n1 = normalize(name1);
        const n2 = normalize(name2);
        return n1 === n2 || n1.includes(n2) || n2.includes(n1);
    }

    /**
     * Cède le contrôle à l'UI pour éviter le blocage
     * Utilise requestIdleCallback si disponible, sinon setTimeout
     */
    yieldToUI() {
        return new Promise(resolve => {
            if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(resolve, { timeout: 5 });
            } else {
                setTimeout(resolve, 0);
            }
        });
    }

    /**
     * Compte les notes éligibles pour l'extraction AI
     * Même logique de filtrage que aiNotesService.processAllNotes()
     * @returns {number} Nombre de notes éligibles
     */
    countEligibleNotes() {
        const { notesCache } = gedcomDataStore;
        if (!notesCache || notesCache.size === 0) return 0;

        return Array.from(notesCache.entries())
            .filter(([id]) => !id.startsWith('e_'))
            .filter(([, noteData]) => (noteData.text || '').trim().length > 10)
            .length;
    }

    /**
     * Configure la proposition d'extraction AI après chargement GEDCOM.
     * L'extraction n'est plus automatique : elle est réservée aux fichiers
     * sauvegardés (currentFileId présent, même signal que l'onglet Photos)
     * et soumise au consentement de l'utilisateur, mémorisé par fichier.
     * Le spinner dans le header est géré automatiquement par backgroundTaskStore
     * qui écoute PROCESSING_STARTED/PROGRESS/COMPLETED/FAILED.
     * Doit être appelé une fois au démarrage de l'application.
     */
    setupExtractionConsent() {
        // Fichier ouvert (cache construit)
        storeEvents.subscribe(EVENTS.CACHE.BUILT, (data) => {
            // Ignorer les restaurations de cache (AI déjà intégrée)
            if (data?.isRestoringFromCache) return;
            setTimeout(() => this.maybeOfferExtraction(), 3000);
        });

        // Fichier ancré dans files-service après coup (sauvegarde d'un
        // import direct) : l'offre n'a pas pu être faite à CACHE.BUILT.
        // allowAutoRun=false : ce signal tire AUSSI à l'ouverture d'un
        // fichier sauvegardé — un consentement mémorisé ne doit pas
        // relancer l'extraction sur un arbre restauré (les événements
        // IA/Wikipedia y sont déjà ; une ré-extraction purge et ré-intègre,
        // ce qui détruisait les événements Wikipedia persistés).
        storeEvents.subscribe(EVENTS.FILE.CURRENT_CHANGED, ({ fileId }) => {
            if (!fileId) return;
            setTimeout(() => this.maybeOfferExtraction({ allowAutoRun: false }), 1000);
        });
    }

    /**
     * Propose l'extraction si toutes les conditions sont réunies :
     * fichier sauvegardé, notes éligibles, pas déjà décidé pour ce fichier.
     * Au plus une offre par fichier et par session (fermer la notification
     * sans choisir = redemandé à la prochaine session).
     */
    maybeOfferExtraction({ allowAutoRun = true } = {}) {
        if (this.isProcessing) return;

        const fileId = configStore.getConfig?.currentFileId;
        if (!fileId) {
            console.log('🤖 AI extraction: fichier non sauvegardé — extraction non proposée');
            return;
        }
        if (this._consentOfferedForFile === fileId) return;

        const eligibleCount = this.countEligibleNotes();
        if (eligibleCount === 0) return;

        const consent = this._getConsent(fileId);
        if (consent === 'denied') return;

        if (consent === 'granted') {
            // Auto-run réservé aux parses frais (CACHE.BUILT non restauré) :
            // sur un arbre restauré les événements IA sont déjà présents.
            if (!allowAutoRun) return;
            console.log(`🤖 AI extraction (consentement mémorisé) : ${eligibleCount} notes`);
            this._consentOfferedForFile = fileId;
            this.integrateAiEventsIntoCache().catch(error => {
                console.warn('AI extraction failed:', error);
            });
            return;
        }

        this._consentOfferedForFile = fileId;
        this._showConsentNotification(fileId, eligibleCount);
    }

    _showConsentNotification(fileId, eligibleCount) {
        notificationManager.actions(
            `<strong>${eligibleCount} note${eligibleCount > 1 ? 's' : ''} biographique${eligibleCount > 1 ? 's' : ''}</strong> détectée${eligibleCount > 1 ? 's' : ''} dans ce fichier.<br>Lancer l'analyse IA pour enrichir la chronologie ?`,
            [
                {
                    label: 'Analyser',
                    cssClass: 'btn-primary',
                    onClick: () => {
                        this._setConsent(fileId, 'granted');
                        this.integrateAiEventsIntoCache().catch(error => {
                            console.warn('AI extraction failed:', error);
                        });
                    }
                },
                {
                    label: 'Non merci',
                    cssClass: 'btn-outline-secondary',
                    onClick: () => this._setConsent(fileId, 'denied')
                }
            ],
            { type: 'info', duration: 0 }
        );
    }

    _getConsent(fileId) {
        try {
            const map = JSON.parse(localStorage.getItem(AI_CONSENT_STORAGE_KEY) || '{}');
            return map[fileId];
        } catch {
            return undefined;
        }
    }

    _setConsent(fileId, value) {
        try {
            const map = JSON.parse(localStorage.getItem(AI_CONSENT_STORAGE_KEY) || '{}');
            map[fileId] = value;
            localStorage.setItem(AI_CONSENT_STORAGE_KEY, JSON.stringify(map));
        } catch (error) {
            console.warn('AI consent storage failed:', error);
        }
    }

    /**
     * Obtient les statistiques d'enrichissement
     */
    getEnrichmentStats() {
        return {
            processedIndividuals: this.processedIndividuals.size,
            totalEventsAdded: Array.from(this.processedIndividuals.values())
                .reduce((sum, data) => sum + data.eventsAdded, 0),
            lastProcessed: Math.max(...Array.from(this.processedIndividuals.values())
                .map(data => data.timestamp))
        };
    }

    /**
     * Réinitialise le service
     */
    reset() {
        this.isProcessing = false;
        this.processedIndividuals.clear();
        this.extractionQueue = [];
    }
}

// Export singleton
const aiNotesIntegration = new AiNotesIntegration();
export default aiNotesIntegration;